import { FC, useCallback, useMemo } from "react";
import { Box, Flex } from "@otrium/core";
import { Trans } from "@lingui/macro";
import { Accordion } from "src/atoms/Accordion";
import {
  ProductBrand,
  SingleProductPaymentTypes,
  WishlistItemType,
  PaymentAndShippingData,
  BrandGoodOnYouInfo,
  PromotionDisplayType,
} from "src/types/graphql.d";
import { useSegment } from "src/hooks/useSegment";
import { useToggleWishlistBrandItem } from "src/hooks/toggleWishlistItem";
import { useDeleteWishlistItem } from "src/hooks/deleteWishlistItem";
import { useAddToWishList } from "src/hooks/useAddToWishlist";
import { ToggleWishListButton } from "../ToggleWishListButton";
import { AccordionHeading, StyledProductMeta } from "./ProductMeta.styled";
import {
  GoodOnYouPillars,
  GoodOnYouRating,
  GoodOnYouCertifications,
} from "src/molecules/GoodOnYou";
import ProductMetaDescription from "./ProductMetaDescription";
import { Locale } from "src/lib/Intl";
import { WishListIconType } from "../ToggleWishListButton/ToggleWishListButton";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { sanitize } from "isomorphic-dompurify";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { GATED_BRAND_TO_FAVOURITE_SOURCE } from "src/constants/gatedItem";
import NoSSR from "src/atoms/NoSSR";
import { lookbookImageLoader } from "src/utils/lookbook-image-loader";
import { ResponsivePicture } from "src/atoms/ResponsivePicture";

export interface ProductMetaProps {
  className?: string;
  locale: string;
  description: string;
  shippingText?: string;
  paymentTypes?: SingleProductPaymentTypes[] | null;
  material?: string;
  brand: ProductBrand;
  fitSize?: string | null;
  careInstructions?: string | null;
  goodOnYouInfo?: BrandGoodOnYouInfo | null;
  openLoginRegisterModal?: (actionSource: string) => void;
}

// See docs/tech/responsive-images.md for why these are density candidates.
const PAYMENT_LOGO_SLOT_HEIGHT = 48;
const PAYMENT_LOGO_DENSITIES = [1, 2, 3];

const ProductMeta: FC<ProductMetaProps> = ({
  className,
  locale,
  description,
  material,
  shippingText,
  paymentTypes,
  brand,
  fitSize,
  careInstructions,
  goodOnYouInfo,
  openLoginRegisterModal,
  ...rest
}) => {
  const { name: brandName } = brand;
  const { data: miscellaneousData } = useMiscellaneous();
  const isLoggedIn = useIsLoggedIn();

  const paymentAndShipping =
    miscellaneousData?.miscellaneous?.pdp?.paymentAndShipping ?? [];
  const { promotionContextState } = usePromotionContext();

  const {
    brandFavourited,
    brandUnfavourited,
    brandFavouritedWhenLoggedOut,
    segmentPdpInfoBoxOpened,
  } = useSegment();

  const { addedToWishlist } = useToggleWishlistBrandItem(brand.id);

  const { deleteWishListItem, loading: deleteLoading } = useDeleteWishlistItem({
    refetchQueries: ["userWishlistMetaData"],
  });

  const { addToWishlist, loading: addLoading } = useAddToWishList({
    refetchQueries: ["userWishlistMetaData"],
  });

  const brandWishlistTracking = useMemo(
    () => ({
      brandName,
      brandId: brand.id,
    }),
    [brand, brandName]
  );

  const onAddToWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      if (isLoggedIn) {
        await addToWishlist(itemType, itemId);
        void brandFavourited(brandWishlistTracking);
        return;
      }
      if (openLoginRegisterModal) {
        openLoginRegisterModal(GATED_BRAND_TO_FAVOURITE_SOURCE);
      }
      void brandFavouritedWhenLoggedOut(brandWishlistTracking);
    },
    [
      isLoggedIn,
      openLoginRegisterModal,
      brandFavouritedWhenLoggedOut,
      brandWishlistTracking,
      addToWishlist,
      brandFavourited,
    ]
  );

  const onRemoveFromWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await deleteWishListItem(itemType, itemId, {});
      void brandUnfavourited(brandWishlistTracking);
    },
    [deleteWishListItem, brandUnfavourited, brandWishlistTracking]
  );

  return (
    <StyledProductMeta className={className} {...rest}>
      <Accordion className="ProductMeta__accordion">
        <Accordion.Panel
          className="ProductMeta__accordionPanel"
          data-testid="meta-product-information"
          onOpen={() => {
            void segmentPdpInfoBoxOpened("Product information");
          }}
        >
          <Accordion.Title>
            <AccordionHeading>
              <Trans>Product information</Trans>
            </AccordionHeading>
          </Accordion.Title>
          <Accordion.Content>
            {description && (
              <Box mt={material || careInstructions ? 2 : 0}>
                <h4 style={{ fontWeight: 600 }}>
                  <Trans>Description</Trans>
                </h4>
                <div
                  data-testid="product-meta-description"
                  dangerouslySetInnerHTML={{ __html: sanitize(description) }}
                />
              </Box>
            )}
            {material && (
              <>
                <h4 style={{ fontWeight: 600 }}>
                  <Trans>Material</Trans>
                </h4>
                <div
                  data-testid="product-meta-material"
                  dangerouslySetInnerHTML={{ __html: sanitize(material) }}
                />
              </>
            )}
            {fitSize && (
              <Box mt={material || careInstructions || description ? 2 : 0}>
                <h4 style={{ fontWeight: 600 }}>
                  <Trans>Size & fit</Trans>
                </h4>
                <div
                  data-testid="product-meta-fit-size"
                  dangerouslySetInnerHTML={{ __html: sanitize(fitSize) }}
                />
              </Box>
            )}
            {careInstructions && (
              <Box mt={material ? 2 : 0}>
                <h4 style={{ fontWeight: 600 }}>
                  <Trans>Care instructions</Trans>
                </h4>
                <div
                  data-testid="product-meta-care-instruction"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(careInstructions),
                  }}
                />
              </Box>
            )}
          </Accordion.Content>
        </Accordion.Panel>

        {goodOnYouInfo && (
          <Accordion.Panel
            className="ProductMeta__accordionPanel"
            data-testid="conscious-product-information"
            onOpen={() => {
              void segmentPdpInfoBoxOpened("Conscious");
            }}
          >
            <Accordion.Title>
              <AccordionHeading>
                <Trans>Ethically rated</Trans>
              </AccordionHeading>
            </Accordion.Title>
            <Accordion.Content>
              <Box pt={2}>
                <GoodOnYouRating
                  scoreMeta={goodOnYouInfo.score_meta}
                  overallScore={goodOnYouInfo.scores.overall}
                  brandName={brandName}
                  pageType="pdp"
                  withSmallHeadings
                />
              </Box>
              <Box my={4}>
                <GoodOnYouPillars
                  pillars={goodOnYouInfo.pillars}
                  scores={goodOnYouInfo.scores}
                />
              </Box>
              {goodOnYouInfo.certifications &&
                goodOnYouInfo.certifications.length > 0 && (
                  <Box my={2}>
                    <GoodOnYouCertifications
                      certifications={goodOnYouInfo.certifications}
                      isRow
                      smallHeadings
                    />
                  </Box>
                )}
            </Accordion.Content>
          </Accordion.Panel>
        )}

        <ProductMetaDescription
          locale={locale as Locale}
          onOpen={() => {
            void segmentPdpInfoBoxOpened("Otrium offers");
          }}
          miscellaneousData={miscellaneousData?.miscellaneous}
        />
        <Accordion.Panel
          className="ProductMeta__accordionPanel"
          onOpen={() => {
            void segmentPdpInfoBoxOpened("Payment and shipping");
          }}
        >
          <Accordion.Title>
            <AccordionHeading>
              <Trans>Payment and shipping</Trans>
            </AccordionHeading>
          </Accordion.Title>
          <Accordion.Content>
            <div className="ProductMeta__paymentProviders">
              {paymentTypes &&
                paymentTypes.map(({ name, image }) => (
                  <div
                    data-testid={`payment-provider-${name}-img`}
                    className="ProductMeta__paymentProviderContainer"
                    key={name}
                  >
                    <ResponsivePicture
                      mobile={{ src: image }}
                      alt={name}
                      densities={PAYMENT_LOGO_DENSITIES}
                      height={PAYMENT_LOGO_SLOT_HEIGHT}
                      loader={lookbookImageLoader}
                      withContainer={false}
                    />
                  </div>
                ))}
            </div>
            {!!paymentAndShipping.length && (
              <Box mt={3} data-testid="shipping-text">
                {paymentAndShipping.map(
                  (paymentAndShippingData: PaymentAndShippingData, index) => (
                    <Box key={index}>
                      <strong>{paymentAndShippingData.title}</strong>
                      <br />
                      {paymentAndShippingData.copy}
                      <br />
                      <br />
                    </Box>
                  )
                )}
              </Box>
            )}
          </Accordion.Content>
        </Accordion.Panel>
      </Accordion>

      {!promotionContextState.promotionDisplayType ||
      promotionContextState.promotionDisplayType !==
        PromotionDisplayType.FlashSale ? (
        <Flex mt={3} flexWrap="wrap">
          <NoSSR>
            <ToggleWishListButton
              dataTestId="follow-brand-wishlist-button"
              itemType={WishlistItemType.Brand}
              itemId={brand.id}
              itemTitle={brandName}
              loading={deleteLoading || addLoading}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onAddToWishList={onAddToWishList}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onRemoveWishList={onRemoveFromWishList}
              isAddedToWishList={addedToWishlist}
              iconType={WishListIconType.HEART_PAGE}
            />
          </NoSSR>
        </Flex>
      ) : null}
    </StyledProductMeta>
  );
};

export default ProductMeta;
